import {
  addMonths,
  eachDayOfInterval,
  format,
  isSameDay,
  isSameMonth,
  isToday,
  startOfMonth,
} from "date-fns";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { useTranslate } from "ra-core";
import { useMemo } from "react";

import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

import { getMonthGridRange } from "./calendarRange";
import { toDateOnly } from "./todoSchedule";
import { useDateLocale } from "./useDateLocale";

export interface MonthCalendarProps {
  month: Date;
  selectedDay: Date;
  /** Days (as `YYYY-MM-DD`) that get a dot because something is planned. */
  markedDays: Set<string>;
  onMonthChange: (month: Date) => void;
  onSelectDay: (day: Date) => void;
}

export const MonthCalendar = ({
  month,
  selectedDay,
  markedDays,
  onMonthChange,
  onSelectDay,
}: MonthCalendarProps) => {
  const translate = useTranslate();
  const { dateFnsLocale } = useDateLocale();
  const weekStartsOn = dateFnsLocale.options?.weekStartsOn ?? 0;

  const days = useMemo(
    () => eachDayOfInterval(getMonthGridRange(month, weekStartsOn)),
    [month, weekStartsOn],
  );
  const weekDayLabels = days
    .slice(0, 7)
    .map((day) => format(day, "EEEEEE", { locale: dateFnsLocale }));

  const goToToday = () => {
    const today = new Date();
    onMonthChange(startOfMonth(today));
    onSelectDay(today);
  };

  return (
    <div className="flex flex-col gap-3">
      <div className="flex items-center justify-between gap-2">
        <h3 className="font-semibold first-letter:uppercase">
          {format(month, "LLLL yyyy", { locale: dateFnsLocale })}
        </h3>
        <div className="flex items-center gap-1">
          <Button variant="outline" size="sm" onClick={goToToday}>
            {translate("crm.personal.calendar.today")}
          </Button>
          <Button
            variant="ghost"
            size="icon"
            aria-label={translate("crm.personal.calendar.previous_month")}
            onClick={() => onMonthChange(addMonths(month, -1))}
          >
            <ChevronLeft />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            aria-label={translate("crm.personal.calendar.next_month")}
            onClick={() => onMonthChange(addMonths(month, 1))}
          >
            <ChevronRight />
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-7 text-center text-xs text-muted-foreground">
        {weekDayLabels.map((label, index) => (
          <span key={index} className="py-1 first-letter:uppercase">
            {label}
          </span>
        ))}
      </div>

      <div className="grid grid-cols-7 gap-1" role="grid">
        {days.map((day) => {
          const isSelected = isSameDay(day, selectedDay);
          const isMarked = markedDays.has(toDateOnly(day));
          return (
            <button
              key={day.toISOString()}
              type="button"
              role="gridcell"
              aria-selected={isSelected}
              aria-label={format(day, "PPPP", { locale: dateFnsLocale })}
              onClick={() => {
                if (!isSameMonth(day, month)) {
                  onMonthChange(startOfMonth(day));
                }
                onSelectDay(day);
              }}
              className={cn(
                "relative flex h-9 items-center justify-center rounded-md text-sm transition-colors hover:bg-accent",
                !isSameMonth(day, month) && "text-muted-foreground/50",
                isToday(day) && !isSelected && "font-semibold text-primary",
                isSelected &&
                  "bg-primary text-primary-foreground hover:bg-primary/90",
              )}
            >
              {format(day, "d")}
              {isMarked && (
                <span
                  aria-hidden
                  className={cn(
                    "absolute bottom-1 size-1 rounded-full",
                    isSelected ? "bg-primary-foreground" : "bg-primary",
                  )}
                />
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
};
